import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { navItems, profile } from "../data/content";
import { useActiveSection } from "../hooks/useActiveSection";
import { Close, FileText, Github, Linkedin, Menu, Moon, Sun } from "./Icons";
import "./Nav.css";

const sectionIds = ["inicio", ...navItems.map((item) => item.id)];

export function Nav({ mode, onToggleTheme }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const activeId = useActiveSection(sectionIds);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const goTo = (id) => (event) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <>
      <header className={`nav ${scrolled ? "nav--scrolled" : ""}`}>
        <div className="container nav__inner">
          <a href="#inicio" className="nav__brand" onClick={goTo("inicio")}>
            {profile.name}
          </a>

          <nav className="nav__links" aria-label="Navegação principal">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={goTo(item.id)}
                className={`nav__link ${activeId === item.id ? "is-active" : ""}`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="nav__actions">
            <button
              type="button"
              className="nav__icon-btn"
              onClick={onToggleTheme}
              aria-label={mode === "dark" ? "Ativar tema claro" : "Ativar tema escuro"}
            >
              {mode === "dark" ? <Sun size={17} /> : <Moon size={17} />}
            </button>

            <a
              className="nav__icon-btn nav__icon-btn--desktop"
              href={profile.github}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <Github size={17} />
            </a>
            <a
              className="nav__icon-btn nav__icon-btn--desktop"
              href={profile.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <Linkedin size={17} />
            </a>

            <a
              className="btn btn--primary btn--sm nav__cv"
              href={profile.resume}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FileText size={16} />
              Currículo
            </a>

            <button
              type="button"
              className="nav__icon-btn nav__burger"
              onClick={() => setOpen(true)}
              aria-label="Abrir menu"
              aria-expanded={open}
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="nav__sheet"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.24 }}
          >
            <div className="container nav__sheet-head">
              <span className="eyebrow">Navegação</span>
              <button
                type="button"
                className="nav__icon-btn"
                onClick={() => setOpen(false)}
                aria-label="Fechar menu"
              >
                <Close size={18} />
              </button>
            </div>

            <nav className="container nav__sheet-links" aria-label="Navegação mobile">
              {navItems.map((item, i) => (
                <motion.a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={goTo(item.id)}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.045, duration: 0.4 }}
                >
                  <span className="nav__sheet-index">0{i + 1}</span>
                  {item.label}
                </motion.a>
              ))}
            </nav>

            <div className="container nav__sheet-foot">
              <a
                className="btn btn--ghost btn--sm"
                href={profile.github}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Github size={16} /> GitHub
              </a>
              <a
                className="btn btn--ghost btn--sm"
                href={profile.linkedin}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin size={16} /> LinkedIn
              </a>
              <a
                className="btn btn--primary btn--sm"
                href={profile.resume}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileText size={16} /> Currículo
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
